/**
 * Zustand store for the Test Cases module.
 *
 * Why Zustand over React Context:
 *  - Components subscribe to specific slices via selector functions.
 *  - FolderTree re-renders ONLY when folder-related state changes.
 *  - BulkActionsBar re-renders ONLY when selectedIds.size changes.
 *  - React Context would re-render every consumer on any state change.
 *
 * Reset on unmount: call useTestCasesStore.getState().reset() in a
 * useEffect cleanup inside TestCasesPage to prevent stale state across
 * navigations.
 */

import { create } from 'zustand';
import type { TestSeverity, TestCaseType, TestType } from '../components/test-cases/types';

export interface ITestCasesFilters {
    severity?: TestSeverity[];
    caseType?: TestCaseType[];
    tags?: string[];
    type?: TestType;
}

interface ITestCasesStore {
    // ── Folder navigation ─────────────────────────────────────────────────────
    selectedFolderId: string | null;
    setSelectedFolderId: (id: string | null) => void;

    // ── Detail pane ───────────────────────────────────────────────────────────
    /** null = nothing selected, '__new__' = create mode, or a test case _id. */
    selectedTestCaseId: string | null;
    setSelectedTestCaseId: (id: string | null) => void;
    detailMode: 'inline' | 'fullscreen';
    setDetailMode: (mode: 'inline' | 'fullscreen') => void;

    // ── Bulk selection ────────────────────────────────────────────────────────
    selectedIds: Set<string>;
    toggleSelected: (id: string) => void;
    selectAll: (ids: string[]) => void;
    clearSelection: () => void;

    // ── Search & Filters ──────────────────────────────────────────────────────
    searchQuery: string;
    setSearchQuery: (q: string) => void;
    filters: ITestCasesFilters;
    setFilters: (f: Partial<ITestCasesFilters>) => void;
    resetFilters: () => void;

    // ── Tree expand state ─────────────────────────────────────────────────────
    expandedFolderIds: Set<string>;
    toggleFolderExpanded: (id: string) => void;
    expandAll: (allIds: string[]) => void;
    collapseAll: () => void;

    // ── Global reset (called on page unmount) ─────────────────────────────────
    reset: () => void;
}

const DEFAULT_STATE = {
    selectedFolderId:   null,
    selectedTestCaseId: null,
    detailMode:         'inline' as const,
    selectedIds:        new Set<string>(),
    searchQuery:        '',
    filters:            {} as ITestCasesFilters,
    expandedFolderIds:  new Set<string>(),
};

export const useTestCasesStore = create<ITestCasesStore>((set) => ({
    ...DEFAULT_STATE,

    // Selecting a folder resets case selection and bulk selection.
    setSelectedFolderId: (id) =>
        set({ selectedFolderId: id, selectedTestCaseId: null, selectedIds: new Set() }),

    setSelectedTestCaseId: (id) => set({ selectedTestCaseId: id }),
    setDetailMode: (mode) => set({ detailMode: mode }),

    toggleSelected: (id) =>
        set((s) => {
            const next = new Set(s.selectedIds);
            next.has(id) ? next.delete(id) : next.add(id);
            return { selectedIds: next };
        }),
    selectAll:      (ids) => set({ selectedIds: new Set(ids) }),
    clearSelection: ()    => set({ selectedIds: new Set() }),

    searchQuery:    '',
    setSearchQuery: (q) => set({ searchQuery: q }),

    filters:      {},
    setFilters:   (f) => set((s) => ({ filters: { ...s.filters, ...f } })),
    resetFilters: ()  => set({ filters: {} }),

    expandedFolderIds: new Set(),
    toggleFolderExpanded: (id) =>
        set((s) => {
            const next = new Set(s.expandedFolderIds);
            next.has(id) ? next.delete(id) : next.add(id);
            return { expandedFolderIds: next };
        }),
    expandAll:   (allIds) => set({ expandedFolderIds: new Set(allIds) }),
    collapseAll: ()       => set({ expandedFolderIds: new Set() }),

    reset: () => set({ ...DEFAULT_STATE, selectedIds: new Set(), expandedFolderIds: new Set() }),
}));
